import { EmptyState } from '@/components/common/empty-state'
import { SkeletonItem } from '@/components/common/skeleton-item'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { CreateWebhookButton } from '@/features/webhooks/components/CreateWebhookButton'
import { DeleteWebhookDialog } from '@/features/webhooks/components/DeleteWebhookDialog'
import { useOrgWebhooks } from '@/features/webhooks/hooks/useWebhooks'
import { Globe, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'

export default function WebhookList() {
  const { data: webhooks, isLoading, error, isError } = useOrgWebhooks()
  const [deleteWebhookId, setDeleteWebhookId] = useState<string | null>(null)

  useEffect(() => {
    if (!isLoading && isError) {
      toast.error('Failed to load webhooks', {
        description:
          error instanceof Error
            ? error.message
            : 'Please try again or contact support',
      })
    }
  }, [isLoading, isError, error])

  return (
    <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div className="mb-8 sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Webhooks</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Manage your webhook endpoints
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <CreateWebhookButton />
        </div>
      </div>

      {/* Webhooks List */}
      <Card>
        <CardHeader className="border-b">
          <CardTitle>Your Webhooks</CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {isLoading ? (
            <SkeletonItem />
          ) : webhooks?.length === 0 ? (
            <EmptyState
              icon={Globe}
              title="No webhooks"
              description="Get started by creating a new webhook endpoint."
            />
          ) : (
            <div className="space-y-4">
              {webhooks?.map((webhook) => (
                <div
                  key={webhook.id}
                  className="flex justify-between items-center p-4 rounded-lg transition-colors duration-150 bg-muted hover:bg-muted/70"
                >
                  <div className="space-y-1">
                    <p className="text-lg font-medium">{webhook.name}</p>
                    <p className="mt-2 text-sm truncate text-muted-foreground">
                      {webhook.url}
                    </p>
                    <p className="text-sm truncate text-muted-foreground">
                      {webhook.secret_preview}
                    </p>
                    <p className="flex flex-wrap gap-2 mt-2">
                      {webhook.event_types.map((event) => (
                        <Badge key={event}>{event}</Badge>
                      ))}
                    </p>
                  </div>
                  <Button
                    variant="destructive"
                    size="sm"
                    aria-label="Delete Webhook"
                    className="inline-flex items-center px-3 py-1 text-sm font-medium leading-4 rounded-md border border-transparent"
                    onClick={() => setDeleteWebhookId(webhook.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <DeleteWebhookDialog
        webhookId={deleteWebhookId}
        onClose={() => setDeleteWebhookId(null)}
      />
    </div>
  )
}
